/* Admin tab , Featured Ticket CTA on the home hero.
   Two modes:
   • Manual , admin picks one ticket and it stays until manually changed.
   • Auto-rotate , admin sets per-ticket "active until" dates. Each day, the
     public endpoint picks the FIRST entry (sorted ascending by active_until)
     where today <= active_until. Once that date passes, it automatically
     switches to the next entry. Falls back to the manual selection if every
     schedule entry has expired. */
import React, { useEffect, useState, useMemo } from "react";
import { Loader2, Save, Check, Ticket, ExternalLink, Calendar, Wand2, MousePointerClick, Plus, Trash2 } from "lucide-react";
import { adminApi } from "./adminApi";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function AdminFeaturedTicket() {
 const [loading, setLoading] = useState(true);
 const [mode, setMode] = useState("manual");
 const [manualId, setManualId] = useState(null);
 const [schedule, setSchedule] = useState([]);
 const [options, setOptions] = useState([]);
 const [original, setOriginal] = useState(null);
 const [saving, setSaving] = useState(false);
 const [savedAt, setSavedAt] = useState(null);
 const [error, setError] = useState("");

 useEffect(() => {
  adminApi("/cms/featured-ticket").then(d => {
   setManualId(d.manual_id || d.id);
   setMode(d.mode || "manual");
   setSchedule(Array.isArray(d.schedule) ? d.schedule : []);
   setOptions(d.options || []);
   setOriginal({ id: d.manual_id || d.id, mode: d.mode || "manual", schedule: d.schedule || [] });
  }).catch(e => setError(e.message)).finally(() => setLoading(false));
 }, []);

 const dirty = useMemo(() => {
  if (!original) return false;
  return JSON.stringify({ id: manualId, mode, schedule }) !== JSON.stringify(original);
 }, [manualId, mode, schedule, original]);

 // Compute which ticket is currently live based on mode + schedule + today
 const liveId = useMemo(() => {
  if (mode !== "auto") return manualId;
  const today = todayISO();
  const valid = [...schedule]
   .filter(s => s.id && s.active_until && options.some(o => o.id === s.id))
   .sort((a, b) => a.active_until.localeCompare(b.active_until));
  for (const s of valid) {
   if (today <= s.active_until) return s.id;
  }
  return manualId; // fallback
 }, [mode, manualId, schedule, options]);

 const save = async () => {
  setSaving(true); setError("");
  try {
   const res = await adminApi("/admin/cms/featured-ticket", { method: "PUT", body: { id: manualId, mode, schedule } });
   setOriginal({ id: res.id, mode: res.mode, schedule: res.schedule || [] });
   setSavedAt(Date.now());
  } catch (e) {
   setError(e.message);
  } finally {
   setSaving(false);
  }
 };

 const discard = () => {
  if (!original) return;
  setManualId(original.id);
  setMode(original.mode);
  setSchedule(original.schedule);
  setSavedAt(null);
 };

 const addScheduleEntry = (id) => {
  // Default the new entry's "active until" to one month from today.
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  setSchedule(s => [...s, { id, active_until: d.toISOString().slice(0, 10) }]);
  setSavedAt(null);
 };

 const updateEntry = (idx, field, value) => {
  setSchedule(s => s.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  setSavedAt(null);
 };

 const removeEntry = (idx) => {
  setSchedule(s => s.filter((_, i) => i !== idx));
  setSavedAt(null);
 };

 if (loading) return <div className="text-slate-500 text-sm">Loading…</div>;

 const activeOpt = options.find(o => o.id === liveId);
 const sortedSchedule = [...schedule].sort((a, b) => (a.active_until || "").localeCompare(b.active_until || ""));

 return (
  <div className="space-y-6" data-testid="admin-featured-ticket">
   <div>
    <h2 className="text-2xl font-black text-slate-900 tracking-tight">Home page · Featured Ticket CTA</h2>
    <p className="text-slate-600 text-sm mt-1">Picks which ticketed cruise shows as the prominent ticket button on the home hero. Use <em>Auto-rotate</em> to set-and-forget the entire event calendar.</p>
   </div>

   {/* Mode toggle */}
   <div className="inline-flex rounded-xl bg-slate-100 ring-1 ring-slate-200 p-1" data-testid="featured-ticket-mode-toggle">
    <button
     onClick={() => { setMode("manual"); setSavedAt(null); }}
     className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${mode === "manual" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
     data-testid="featured-ticket-mode-manual"
    >
     <MousePointerClick className="w-4 h-4" /> Manual
    </button>
    <button
     onClick={() => { setMode("auto"); setSavedAt(null); }}
     className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${mode === "auto" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
     data-testid="featured-ticket-mode-auto"
    >
     <Wand2 className="w-4 h-4" /> Auto-rotate
    </button>
   </div>

   {/* MANUAL , option grid */}
   {mode === "manual" && (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="featured-ticket-options">
     {options.map(opt => {
      const selected = opt.id === manualId;
      return (
       <button
        key={opt.id}
        onClick={() => { setManualId(opt.id); setSavedAt(null); }}
        className={`text-left rounded-2xl ring-1 p-5 transition-all ${selected ? "bg-slate-900 text-white ring-slate-900 shadow-lg" : "bg-white ring-slate-200 hover:ring-slate-400 hover:bg-slate-50 text-slate-900"}`}
        data-testid={`featured-ticket-option-${opt.id}`}
       >
        <div className="flex items-center justify-between gap-3 mb-2">
         <div className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-black ${selected ? "text-amber-300" : "text-slate-500"}`}>
          <Ticket className="w-3.5 h-3.5" /> {opt.id}
         </div>
         {selected && <span className="text-[9px] uppercase tracking-[0.22em] font-black bg-amber-300 text-slate-900 px-2 py-0.5 rounded-full">Selected</span>}
        </div>
        <div className="font-black text-base tracking-tight">{opt.label}</div>
        <div className={`text-xs mt-1 ${selected ? "text-slate-300" : "text-slate-600"}`}>{opt.price_label}</div>
       </button>
      );
     })}
    </div>
   )}

   {/* AUTO , schedule editor */}
   {mode === "auto" && (
    <div className="space-y-4" data-testid="featured-ticket-schedule">
     <div className="rounded-2xl bg-blue-50 ring-1 ring-blue-200 p-4 text-sm text-blue-900">
      <strong>How auto-rotate works:</strong> entries are sorted by their <em>active until</em> date. Each day at 00:00, the system shows the FIRST entry whose <em>active until</em> is today or later. Once that date passes, it switches automatically to the next entry. If every entry has expired, the <strong>Manual fallback</strong> below is shown.
     </div>

     <div className="space-y-2.5">
      {sortedSchedule.length === 0 && (
       <div className="rounded-xl bg-slate-50 ring-1 ring-dashed ring-slate-300 px-5 py-8 text-center text-sm text-slate-500">
        No schedule entries yet. Click an option below to add one.
       </div>
      )}
      {sortedSchedule.map((entry, idx) => {
       const opt = options.find(o => o.id === entry.id);
       const isLive = entry.id === liveId;
       // The original (unsorted) index for editing
       const realIdx = schedule.findIndex(s => s === entry);
       return (
        <div key={`${entry.id}-${realIdx}`} className={`flex flex-wrap items-center gap-3 rounded-xl ring-1 p-4 ${isLive ? "bg-amber-50 ring-amber-300" : "bg-white ring-slate-200"}`} data-testid={`schedule-row-${idx}`}>
         <div className="flex items-center gap-2 min-w-0 flex-1">
          <Ticket className={`w-4 h-4 shrink-0 ${isLive ? "text-amber-600" : "text-slate-400"}`} />
          <div className="min-w-0">
           <div className="font-black text-sm text-slate-900 truncate flex items-center gap-2">
            {opt?.label || entry.id}
            {isLive && <span className="text-[9px] uppercase tracking-[0.22em] font-black bg-amber-400 text-slate-900 px-1.5 py-0.5 rounded-full">Live now</span>}
           </div>
           <div className="text-[11px] text-slate-500">{opt?.price_label}</div>
          </div>
         </div>
         <div className="flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Active until</label>
          <input
           type="date"
           value={entry.active_until || ""}
           onChange={(e) => updateEntry(realIdx, "active_until", e.target.value)}
           className="rounded-lg ring-1 ring-slate-300 bg-white px-3 py-1.5 text-sm font-mono text-slate-900 focus:ring-2 focus:ring-slate-500 outline-none"
           data-testid={`schedule-date-${idx}`}
          />
         </div>
         <button
          onClick={() => removeEntry(realIdx)}
          className="w-9 h-9 rounded-lg bg-white ring-1 ring-slate-200 hover:ring-rose-300 hover:bg-rose-50 hover:text-rose-600 text-slate-400 flex items-center justify-center transition-colors"
          data-testid={`schedule-remove-${idx}`}
          aria-label="Remove entry"
         >
          <Trash2 className="w-4 h-4" />
         </button>
        </div>
       );
      })}
     </div>

     <div>
      <div className="text-[10px] uppercase tracking-[0.28em] font-black text-slate-500 mb-2">Add to schedule</div>
      <div className="flex flex-wrap gap-2">
       {options.map(opt => (
        <button
         key={opt.id}
         onClick={() => addScheduleEntry(opt.id)}
         className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white ring-1 ring-slate-200 hover:ring-slate-400 hover:bg-slate-50 text-slate-900 text-xs font-bold transition-all"
         data-testid={`schedule-add-${opt.id}`}
        >
         <Plus className="w-3 h-3" /> {opt.label}
        </button>
       ))}
      </div>
     </div>

     {/* Manual fallback selector , shown when every schedule entry has expired */}
     <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-4">
      <div className="text-[10px] uppercase tracking-[0.28em] font-black text-slate-500 mb-2 flex items-center gap-2">
       <Calendar className="w-3.5 h-3.5" /> Fallback when schedule expires
      </div>
      <select
       value={manualId || ""}
       onChange={(e) => { setManualId(e.target.value); setSavedAt(null); }}
       className="rounded-lg ring-1 ring-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-900 focus:ring-2 focus:ring-slate-500 outline-none w-full sm:w-auto"
       data-testid="featured-ticket-fallback"
      >
       {options.map(o => <option key={o.id} value={o.id}>{o.label} · {o.price_label}</option>)}
      </select>
     </div>
    </div>
   )}

   {/* Live preview */}
   {activeOpt && (
    <div className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-5" data-testid="featured-ticket-preview">
     <div className="text-[10px] uppercase tracking-[0.28em] font-black text-slate-500 mb-3 flex items-center gap-2">
      Live preview {mode === "auto" && <span className="text-amber-700">· based on today</span>}
     </div>
     <div className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-slate-900 text-white font-black text-sm tracking-widest uppercase">
      <Ticket className="w-5 h-5" /> {activeOpt.label}{activeOpt.price_label ? ` · ${activeOpt.price_label}` : ""}
     </div>
     <a href={activeOpt.href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 mt-3 ml-2">
      Opens <code className="bg-white ring-1 ring-slate-200 px-1.5 py-0.5 rounded text-[11px]">{activeOpt.href}</code> <ExternalLink className="w-3 h-3" />
     </a>
    </div>
   )}

   <div className="flex items-center gap-3 pt-2">
    <button onClick={save} disabled={!dirty || saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-testid="featured-ticket-save">
     {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
     {saving ? "Saving…" : "Save"}
    </button>
    {dirty && !saving && (
     <button onClick={discard} className="text-sm text-slate-500 hover:text-slate-700 underline-offset-2 hover:underline" data-testid="featured-ticket-reset">
      Discard
     </button>
    )}
    {savedAt && !dirty && (
     <div className="inline-flex items-center gap-1.5 text-emerald-600 text-sm font-medium" data-testid="featured-ticket-saved">
      <Check className="w-4 h-4" /> Saved · live on home page
     </div>
    )}
    {error && <div className="text-rose-600 text-sm font-medium" data-testid="featured-ticket-error">{error}</div>}
   </div>
  </div>
 );
}
